import { useState, useRef, useEffect } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { hasPageAccess } from "@/utils/access";
import { isNavigationActive } from "@/utils/navigation";
import {
  LayoutDashboard, FilePlus, FileText, Layers, Package,
  Activity, Settings, LogOut, Building2, ChevronDown,
  TrendingUp, Truck, ShoppingCart,
  BarChart3, BookOpen, Menu, X, Users, PackageX,
} from "lucide-react";

// ── Nav structure ──────────────────────────────────────────────────────────
// Each dropdown item has `groups`: an array of { heading, children }. A
// `heading: null` group renders as a flat list (no sub-label); a non-null
// heading renders a small uppercase section label above its children --
// used to nest related-but-distinct workflows (Invoices vs. Allocations)
// under one top-level item instead of giving each its own top-level slot.
// Consolidated from 8 top-level items down to 6 (merged "Supplementary Cost
// Invoices" + "Cost Allocations" into one "Supplementary Costs" dropdown
// with 2 sub-groups, and "Audit Log" + "Supplier Charges Report" into one
// "Reports" dropdown) after the previous flat 8-item layout started
// visually overlapping the SAP status badge / user menu on common screen
// widths once "Supplier Charges Report" was added as its own top-level item.
const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, page: "dashboard" },
  {
    label: "Invoicing",
    icon: FileText,
    groups: [{ heading: null, children: [
      { to: "/invoices/new",          label: "Supplier Invoice Register", icon: FilePlus, page: "invoice_new" },
      { to: "/returns",               label: "Returns to Supplier",       icon: PackageX, page: "invoice_history" },
      { to: "/supplier-ledger",       label: "Supplier Ledger",           icon: Users, page: "supplier_ledger" },
    ] }],
  },
  {
    label: "Supplier POs",
    icon: ShoppingCart,
    groups: [{ heading: null, children: [
      { to: "/supplier-pos?tab=bom",  label: "BOM / Raw Material POs",             icon: Package, page: "supplier_pos" },
      { to: "/supplier-pos?tab=cons", label: "Consumables / Capital / Services",   icon: BarChart3, page: "supplier_pos" },
      { to: "/supplier-pos?tab=unclassified", label: "Unclassified SAP POs", icon: Package, page: "supplier_pos" },
    ] }],
  },
  {
    label: "Supplementary Costs",
    icon: Truck,
    groups: [
      { heading: "Invoices", children: [
        { to: "/supplementary-invoices/new", label: "Add Supplementary Invoice", icon: FilePlus, page: "landed_cost_invoices" },
        { to: "/supplementary-invoices",     label: "Supplementary Invoice Register", icon: FileText, page: "landed_cost_invoices" },
        { to: "/supplementary-invoices/customs-duty", label: "Customs Duty & BOE", icon: BookOpen, page: "customs_duty" },
      ] },
      { heading: "Allocations", children: [
        { to: "/cost-allocations?view=workbench", label: "Allocate Supplementary Costs", icon: Layers, page: "allocations" },
        { to: "/cost-allocations?view=register", label: "Allocation Register", icon: BookOpen, page: "allocations" },
      ] },
    ],
  },
  {
    label: "Reports",
    icon: BarChart3,
    groups: [{ heading: null, children: [
      { to: "/reports/supplier-charges", label: "Supplier Charges Report", icon: BarChart3, page: "supplier_charges_report" },
      { to: "/audit-log", label: "Audit Log", icon: Activity, page: "audit_log" },
    ] }],
  },
  {
    label: "Admin", icon: Settings,
    groups: [{ heading: null, children: [
      { to: "/access-management", label: "Access Management", icon: Settings, page: "access_management" },
      { to: "/sap-syncs", label: "SAP Sync Control", icon: Activity, page: "sap_syncs" },
      { to: "/gl-mappings", label: "Supplementary Cost G/Ls", icon: Settings, page: "gl_mappings" },
      { to: "/settings", label: "System Settings", icon: Settings, page: "settings" },
    ] }],
  },
];

const PAGE_META = {
  "/dashboard":                  { title: "Dashboard",                  sub: "Operations Overview" },
  "/invoices/new":               { title: "Supplier Invoice Register",   sub: "All due, draft, and posted goods invoices" },
  "/returns":                    { title: "Returns to Supplier",         sub: "Quality rejections, damage, over-shipments · credit memos auto-stubbed" },
  "/invoices/debit-note":        { title: "Supplier Debit Notes",        sub: "Raise debit notes against suppliers" },
  "/invoices/credit-note":       { title: "Credit Notes",               sub: "Supplier credit note management" },
  "/supplier-ledger":            { title: "Supplier Ledger",             sub: "Live SAP account view: invoices, credit memos, subs debit/credit" },
  "/supplier-pos":               { title: "Supplier Purchase Orders",    sub: "Read-only · SAP ByDesign" },
  "/supplementary-invoices":     { title: "Supplementary Cost Invoices", sub: "Freight, loading, customs and other post-purchase costs" },
  "/supplementary-invoices/new": { title: "Add Supplementary Invoice",   sub: "Capture costs for later allocation to goods invoices" },
  "/supplementary-invoices/customs-duty": { title: "Customs Duty & BOE", sub: "Bill of Entry reconciliation for supplementary costs" },
  "/cost-allocations":           { title: "Cost Allocations",             sub: "Allocate supplementary invoice costs to goods invoices" },
  "/audit-log":                  { title: "Audit Log",                  sub: "Full SAP call trail" },
  "/reports/supplier-charges":   { title: "Supplier Charges Report",    sub: "Material value vs. Freight, Loading, Unloading, Packing, Other & Round Off — pre-tax, synced from SAP" },
  "/settings":                   { title: "Administration",             sub: "System configuration" },
  "/access-management":          { title: "IT Access Management",        sub: "Page-level user permissions" },
  "/sap-syncs":                  { title: "SAP Sync Control",             sub: "Cache status and worker utilization" },
  "/gl-mappings":                { title: "Supplementary Cost G/L Mappings", sub: "Editable finance mapping controls" },
};

// ── Dropdown nav item ──────────────────────────────────────────────────────
function DropdownNavItem({ item, location }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const isActive = item.groups.some((g) => g.children.some((child) => isNavigationActive(location, child.to)));

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid={`nav-${item.label.toLowerCase().replace(/ /g, "-")}`}
        aria-expanded={open}
        data-active={isActive}
        className={`flex items-center gap-1 px-3 py-2 rounded-md text-[13px] font-bold whitespace-nowrap transition-colors ${
          isActive
            ? "text-white bg-white/15"
            : "text-white/70 hover:text-white hover:bg-white/10"
        }`}
      >
        {item.label}
        <ChevronDown size={12} className={`transition-transform ${open ? "rotate-180" : ""}`} />
        {isActive && (
          <span
            className="absolute -bottom-0 left-1/2 -translate-x-1/2 w-4 h-0.5 rounded-full"
            style={{ backgroundColor: "#9CE3D9" }}
          />
        )}
      </button>

      {open && (
        <div data-testid={`nav-dropdown-${item.label.toLowerCase().replace(/ /g, "-")}`} className="absolute top-full left-0 mt-1 w-64 bg-white border border-slate-200 rounded-md shadow-md py-1 z-50">
          {item.groups.map((group, gi) => (
            <div key={group.heading || gi} className={gi > 0 ? "mt-1 pt-1 border-t border-slate-100" : ""}>
              {group.heading && (
                <p className="px-3 pt-1.5 pb-1 text-[10px] font-bold uppercase text-slate-400" data-testid={`nav-dropdown-group-${group.heading.toLowerCase()}`}>
                  {group.heading}
                </p>
              )}
              {group.children.map((child) => {
                const isChildActive = isNavigationActive(location, child.to);
                return (
                  <Link
                    key={child.to}
                    to={child.to}
                    onClick={() => setOpen(false)}
                    data-testid={`nav-child-${child.label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "")}`}
                    aria-current={isChildActive ? "page" : undefined}
                    data-active={isChildActive}
                    className={`flex items-center gap-2 px-3 py-2 text-[13px] font-semibold transition-colors ${
                      isChildActive
                        ? "text-[#0A838A] font-bold bg-[#0A838A]/5"
                        : "text-slate-600 hover:text-slate-900 hover:bg-slate-50"
                    }`}
                  >
                    <child.icon size={12} className={isChildActive ? "text-[#0A838A]" : "text-slate-400"} />
                    {child.label}
                    {isChildActive && (
                      <span className="ml-auto w-1.5 h-1.5 rounded-full bg-[#0A838A]" />
                    )}
                  </Link>
                );
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function MobileNavLink({ item, location, onNavigate }) {
  const isActive = isNavigationActive(location, item.to);
  const Icon = item.icon;
  return <Link
    to={item.to}
    onClick={onNavigate}
    data-testid={`mobile-nav-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "")}`}
    aria-current={isActive ? "page" : undefined}
    data-active={isActive}
    className={`flex min-w-0 items-center gap-2 px-2.5 py-2 text-[12px] font-bold transition-colors ${isActive ? "bg-[#0A838A]/5 text-[#076D74]" : "text-slate-700 hover:bg-[#F2F9F8] hover:text-[#0A838A]"}`}
  >
    <Icon size={13} className={isActive ? "shrink-0 text-[#0A838A]" : "shrink-0 text-slate-400"} />
    <span className="min-w-0 break-words">{item.label}</span>
  </Link>;
}

// ── Main Layout ────────────────────────────────────────────────────────────
export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const userMenuRef = useRef(null);

  const basePath = "/" + location.pathname.split("/").slice(1, 3).join("/");
  const page = PAGE_META[location.pathname] || PAGE_META[basePath] || { title: "Supplier Finance Hub", sub: "" };

  useEffect(() => {
    document.title = page.title === "Supplier Finance Hub" ? page.title : `${page.title} · Supplier Finance Hub`;
  }, [page.title]);

  const visibleNav = NAV.map((item) => {
    if (!item.groups) return hasPageAccess(user, item.page) ? item : null;
    const groups = item.groups
      .map((g) => ({ ...g, children: g.children.filter((child) => hasPageAccess(user, child.page)) }))
      .filter((g) => g.children.length);
    return groups.length ? { ...item, groups } : null;
  }).filter(Boolean);

  useEffect(() => {
    const h = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) setUserMenuOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  return (
    <div className="flex flex-col min-h-screen bg-[#F2F6F6]">
      {/* ── Top Nav ── */}
      <header
        className="sticky top-0 z-50 flex items-center px-3 sm:px-5 h-16 shrink-0 w-full max-w-full"
        style={{ backgroundColor: "#0A838A" }}
      >
        {/* Brand */}
        <div className="flex items-center gap-2.5 mr-2 sm:mr-6 shrink-0">
          <div
            className="w-9 h-9 rounded-md flex items-center justify-center"
            style={{ backgroundColor: "rgba(255,255,255,0.15)" }}
          >
            <Building2 size={18} className="text-white" />
          </div>
          <div className="leading-none hidden sm:block">
            <p className="text-[16px] font-heading font-bold text-white">Supplier Finance Hub</p>
          </div>
        </div>

        {/* Nav -- full row only renders at `xl` (1280px+); narrower windows
            fall back to the hamburger + slide-down mobile menu below
            instead of a nav row that can't fit all 6 top-level items plus
            the SAP status badge + user menu without visually overlapping
            them (live-caught Feb 2026 -- flex children with
            `whitespace-nowrap` text can't shrink below their own label
            width, so the row simply painted past its box into the
            right-side content rather than actually getting narrower). */}
        <nav className="hidden xl:flex items-center gap-0.5 flex-1 min-w-0 overflow-visible">
          {visibleNav.map((item) => {
            if (item.groups) {
              return <DropdownNavItem key={item.label} item={item} location={location} />;
            }
            return (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`nav-${item.label.toLowerCase().replace(/ /g, "-")}`}
                className={({ isActive }) =>
                  `relative flex items-center gap-1.5 px-3 py-2 rounded-md text-[13px] font-bold whitespace-nowrap transition-colors ${
                    isActive
                      ? "text-white bg-white/15"
                      : "text-white/70 hover:text-white hover:bg-white/10"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    {item.label}
                    {isActive && (
                      <span
                        className="absolute -bottom-0 left-1/2 -translate-x-1/2 w-4 h-0.5 rounded-full"
                        style={{ backgroundColor: "#9CE3D9" }}
                      />
                    )}
                  </>
                )}
              </NavLink>
            );
          })}
        </nav>

        <button
          onClick={() => setMobileMenuOpen((open) => !open)}
          data-testid="mobile-navigation-menu-button"
          className="xl:hidden ml-auto w-8 h-8 flex items-center justify-center text-white hover:bg-white/10 transition-colors"
          aria-label={mobileMenuOpen ? "Close navigation menu" : "Open navigation menu"}
        >
          {mobileMenuOpen ? <X size={18} /> : <Menu size={18} />}
        </button>

        {/* Right badges + user */}
        <div className="flex items-center gap-2 shrink-0 ml-2 sm:ml-4">
          <div
            className="hidden xl:flex items-center gap-1.5 rounded-full px-3 py-1.5"
            style={{ backgroundColor: "rgba(255,255,255,0.12)" }}
            data-testid="sap-status-badge"
          >
            <div className="w-1.5 h-1.5 rounded-full bg-[#6EDD5B]" />
            <span className="text-[12.5px] font-bold text-white/90">SAP PRD</span>
            <TrendingUp size={12} style={{ color: "#9CE3D9" }} />
          </div>

          {/* User dropdown */}
          <div className="relative" ref={userMenuRef}>
            <button
              onClick={() => setUserMenuOpen((v) => !v)}
              data-testid="user-menu-button"
              className="flex items-center gap-2 px-2.5 py-1.5 rounded-md text-white/90 hover:text-white hover:bg-white/10 transition-colors"
            >
              <div
                className="w-7 h-7 rounded-full flex items-center justify-center text-white text-[12.5px] font-bold shrink-0"
                style={{ backgroundColor: "rgba(255,255,255,0.25)" }}
              >
                {(user?.display_name || user?.email || "U")[0].toUpperCase()}
              </div>
              <span className="hidden md:block text-[13.5px] font-bold max-w-[110px] truncate">
                {user?.display_name || user?.email?.split("@")[0]}
              </span>
              <ChevronDown size={13} className={`transition-transform ${userMenuOpen ? "rotate-180" : ""}`} />
            </button>

            {userMenuOpen && (
              <div className="absolute right-0 top-full mt-1 w-52 bg-white border border-slate-200 rounded-lg shadow-lg py-1 z-50">
                <div className="px-3 py-2 border-b border-slate-100">
                  <p className="text-[12px] font-medium text-slate-900 truncate">
                    {user?.display_name || user?.email}
                  </p>
                  <p className="text-[10px] text-slate-400 font-mono uppercase mt-0.5">
                    {user?.role?.replace("_", " ") || "user"}
                  </p>
                </div>
                <button
                  onClick={() => { setUserMenuOpen(false); logout(); navigate("/login"); }}
                  data-testid="logout-button"
                  className="w-full flex items-center gap-2 px-3 py-2 text-[13px] text-slate-600 hover:bg-red-50 hover:text-red-600 transition-colors"
                >
                  <LogOut size={13} />
                  Sign out
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {mobileMenuOpen && <nav className="xl:hidden absolute top-16 inset-x-3 z-40 max-h-[calc(100vh-5rem)] overflow-y-auto border border-teal-100 bg-white p-4 shadow-lg" data-testid="mobile-navigation-menu">
        <div className="grid grid-cols-2 gap-2 border-b border-slate-100 pb-3">
          {visibleNav.filter((item) => !item.groups).map((item) => <MobileNavLink key={item.to} item={item} location={location} onNavigate={() => setMobileMenuOpen(false)} />)}
        </div>
        <div className="grid gap-x-6 gap-y-5 py-4 sm:grid-cols-2">
          {visibleNav.filter((item) => item.groups).map((group) => <section key={group.label} data-testid={`mobile-nav-group-${group.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}><p className="mb-1.5 text-[10px] font-bold uppercase text-slate-400">{group.label}</p><div className="space-y-0.5">{group.groups.flatMap((g) => g.children).map((item) => <MobileNavLink key={item.to} item={item} location={location} onNavigate={() => setMobileMenuOpen(false)} />)}</div></section>)}
        </div>
      </nav>}

      {/* ── Sub-header ── */}
      <div className="bg-white border-b border-slate-200 px-6 py-3 shrink-0">
        <h1 className="text-[15px] font-heading font-bold text-slate-900">{page.title}</h1>
        {page.sub && (
          <p className="text-[11px] text-slate-400 font-mono mt-0.5">{page.sub}</p>
        )}
      </div>

      {/* ── Content ── */}
      <main className="flex-1 overflow-y-auto p-6">
        {children}
      </main>
    </div>
  );
}
